import { useState, useEffect, useCallback, useRef } from "react";
import { generateCSSTransform } from "../utils/newPerspectiveWarp";

const INITIAL_DOOR_DIMENSIONS = {
  WIDTH: 225,
  HEIGHT: 100,
};

export const useMobileDragging = (points, setPoints, image) => {
  const containerRef = useRef(null);
  const [draggingHandle, setDraggingHandle] = useState(null);
  const [isDraggingDoor, setIsDraggingDoor] = useState(false);
  const [dragStartOffset, setDragStartOffset] = useState(null);
  const [showHandles, setShowHandles] = useState(true);
  const [imageTransform, setImageTransform] = useState(
    "matrix3d(1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1)"
  );

  // Update transform when points change
  useEffect(() => {
    if (image && points.length === 4) {
      const srcPoints = [
        { x: 0, y: 0 },
        { x: INITIAL_DOOR_DIMENSIONS.WIDTH, y: 0 },
        { x: INITIAL_DOOR_DIMENSIONS.WIDTH, y: INITIAL_DOOR_DIMENSIONS.HEIGHT },
        { x: 0, y: INITIAL_DOOR_DIMENSIONS.HEIGHT },
      ];
      
      const transform = generateCSSTransform(srcPoints, points);
      setImageTransform(transform);
    }
  }, [points, image]);

  const getMousePositionInContainer = (clientX, clientY) => {
    if (!containerRef.current || typeof clientX !== "number" || typeof clientY !== "number") {
      return null;
    }
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(clientX - rect.left, rect.width)),
      y: Math.max(0, Math.min(clientY - rect.top, rect.height)),
    };
  };

  const handleMouseDownOnHandle = (e, id) => {
    // Don't call preventDefault here - let the DraggableHandle component handle it
    e.stopPropagation();

    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const mousePos = getMousePositionInContainer(clientX, clientY);
    if (!mousePos) return;

    setDraggingHandle(id);
    setIsDraggingDoor(false);
  };

  const handleMouseDownOnDoor = (e) => {
    if (draggingHandle !== null) return;

    // Only prevent default for mouse events, not touch events
    if (e.type === 'mousedown') {
      e.preventDefault();
    }

    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const mousePos = getMousePositionInContainer(clientX, clientY);
    if (!mousePos || points.length !== 4) return;

    // Calculate centroid for better door dragging
    const centroidX = (points[0].x + points[1].x + points[2].x + points[3].x) / 4;
    const centroidY = (points[0].y + points[1].y + points[2].y + points[3].y) / 4;

    setDragStartOffset({
      x: mousePos.x - centroidX,
      y: mousePos.y - centroidY,
    });
    setIsDraggingDoor(true);
  };

  const handleTouchStartOnDoor = (e) => {
    if (draggingHandle !== null) return;
    
    // This will be registered as non-passive, so preventDefault works
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    const mousePos = getMousePositionInContainer(touch.clientX, touch.clientY);
    if (!mousePos || points.length !== 4) return;

    // Calculate centroid for better door dragging
    const centroidX = (points[0].x + points[1].x + points[2].x + points[3].x) / 4;
    const centroidY = (points[0].y + points[1].y + points[2].y + points[3].y) / 4;

    setDragStartOffset({
      x: mousePos.x - centroidX,
      y: mousePos.y - centroidY,
    });
    setIsDraggingDoor(true);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!containerRef.current) return;

      let clientX, clientY;
      if (e instanceof MouseEvent) {
        clientX = e.clientX;
        clientY = e.clientY;
      } else if (e.touches && e.touches[0]) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        return;
      }

      const mousePos = getMousePositionInContainer(clientX, clientY);
      if (!mousePos) return;

      if (draggingHandle !== null) {
        // Dragging individual corner
        const newPoints = [...points];
        newPoints[draggingHandle] = { x: mousePos.x, y: mousePos.y };
        setPoints(newPoints);
      } else if (isDraggingDoor && dragStartOffset && points.length === 4) {
        // Dragging entire door
        const centroidX = (points[0].x + points[1].x + points[2].x + points[3].x) / 4;
        const centroidY = (points[0].y + points[1].y + points[2].y + points[3].y) / 4;
        
        const newCentroidX = mousePos.x - dragStartOffset.x;
        const newCentroidY = mousePos.y - dragStartOffset.y;
        
        const dx = newCentroidX - centroidX;
        const dy = newCentroidY - centroidY;

        const containerWidth = containerRef.current?.clientWidth || 400;
        const containerHeight = containerRef.current?.clientHeight || 300;

        const newPoints = points.map((point) => ({
          x: Math.max(0, Math.min(point.x + dx, containerWidth)),
          y: Math.max(0, Math.min(point.y + dy, containerHeight)),
        }));
        setPoints(newPoints);
      }
    },
    [draggingHandle, isDraggingDoor, points, dragStartOffset, setPoints]
  );

  const handleMouseUp = useCallback(() => {
    setDraggingHandle(null);
    setIsDraggingDoor(false);
    setDragStartOffset(null);
  }, []);

  useEffect(() => {
    const currentMouseMove = (e) => handleMouseMove(e);
    const currentTouchMove = (e) => {
      e.preventDefault(); // Prevent scrolling while dragging
      handleMouseMove(e);
    };
    const currentMouseUp = () => handleMouseUp();
    const currentTouchEnd = () => handleMouseUp();

    if (draggingHandle !== null || isDraggingDoor) {
      document.addEventListener("mousemove", currentMouseMove);
      document.addEventListener("mouseup", currentMouseUp);
      document.addEventListener("touchmove", currentTouchMove, {
        passive: false, // Non-passive so preventDefault works
      });
      document.addEventListener("touchend", currentTouchEnd);
    }

    return () => {
      document.removeEventListener("mousemove", currentMouseMove);
      document.removeEventListener("touchmove", currentTouchMove);
      document.removeEventListener("mouseup", currentMouseUp);
      document.removeEventListener("touchend", currentTouchEnd);
    };
  }, [draggingHandle, isDraggingDoor, handleMouseMove, handleMouseUp]);

  return {
    containerRef,
    draggingHandle,
    isDraggingDoor,
    showHandles,
    imageTransform,
    handleMouseDownOnHandle,
    handleMouseDownOnDoor,
    handleTouchStartOnDoor,
    setShowHandles,
  };
};